import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import type { ApiClient } from "./api.js";
import type { Auth } from "./auth.js";
import type { AppConfig } from "./config.js";
import { matchRoute, navigate, onRouteChange } from "./router.js";
import type { RouteMatch } from "./router.js";
import { sharedStyles } from "./styles.js";
import "./views/stream.js";
import "./views/search.js";
import "./views/kept.js";
import "./views/feeds.js";
import "./views/rules.js";
import "./views/rule-activity.js";

const NAV: ReadonlyArray<{ path: string; label: string }> = [
  { path: "/", label: "Stream" },
  { path: "/search", label: "Search" },
  { path: "/kept", label: "Kept" },
  { path: "/feeds", label: "Feeds" },
  { path: "/rules", label: "Rules" },
];

/** Root shell: config/auth bootstrap gate, top nav, and the route outlet. */
export class NfApp extends LitElement {
  static override properties = {
    config: { attribute: false },
    auth: { attribute: false },
    api: { attribute: false },
    ready: { attribute: false },
    bootError: { attribute: false },
    route: { state: true },
  };

  config?: AppConfig;
  auth?: Auth;
  api?: ApiClient;
  ready = false;
  bootError?: string;
  route: RouteMatch = matchRoute(window.location.pathname);
  private unlisten?: () => void;

  static override styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--nf-bg);
      }
      header {
        container-type: inline-size;
        position: sticky;
        top: 0;
        z-index: 10;
        display: flex;
        align-items: center;
        gap: var(--nf-space-12);
        padding: var(--nf-space-10) var(--nf-space-16);
        background: var(--nf-surface);
        border-bottom: 1px solid var(--nf-border);
      }
      .brand {
        font-weight: 700;
        color: var(--nf-accent);
        margin-right: var(--nf-space-8);
      }
      nav {
        display: flex;
        gap: var(--nf-space-4);
        flex: 1;
      }
      nav a {
        padding: var(--nf-space-6) var(--nf-space-10);
        border-radius: var(--nf-radius);
        text-decoration: none;
        color: var(--nf-text);
      }
      nav a.active {
        background: var(--nf-surface-2);
        color: var(--nf-accent);
        font-weight: 600;
      }
      header label {
        display: none;
      }
      @container (width < 700px) {
        nav {
          display: none;
          flex-direction: column;
          position: absolute;
          right: 0.25rem;
          top: 0.25rem;
          background: var(--ctp-surface1);
          padding: 0.5rem;
          border-radius: 0.5rem;
        }
        header label {
          display: inline;
          order: 1;
          margin-left: auto;
          margin-right: 5rem;
        }
        label:has([type=checkbox]:checked) ~ nav {
          display: flex;
        }
      }
      main {
        max-width: 900px;
        margin: 0 auto;
        padding: var(--nf-space-16);
      }
      .gate {
        display: grid;
        place-items: center;
        min-height: 100vh;
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    this.unlisten = onRouteChange(() => {
      this.route = matchRoute(window.location.pathname);
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unlisten?.();
  }

  private go(event: Event, path: string): void {
    event.preventDefault();
    navigate(path);
  }

  private renderView(): TemplateResult {
    const { name, params } = this.route;
    const api = this.api;
    if (!api) return html`<div class="empty">Loading…</div>`;
    switch (name) {
      case "feed":
        return html`<nf-stream-view
          .api=${api}
          .feedId=${params.id ?? null}
        ></nf-stream-view>`;
      case "search":
        return html`<nf-search-view .api=${api}></nf-search-view>`;
      case "kept":
        return html`<nf-kept-view .api=${api}></nf-kept-view>`;
      case "feeds":
        return html`<nf-feeds-view .api=${api}></nf-feeds-view>`;
      case "rules":
        return html`<nf-rules-view .api=${api}></nf-rules-view>`;
      case "ruleActivity":
        return html`<nf-rule-activity-view
          .api=${api}
          .ruleId=${params.id ?? ""}
        ></nf-rule-activity-view>`;
      default:
        return html`<nf-stream-view
          .api=${api}
          .feedId=${null}
        ></nf-stream-view>`;
    }
  }

  override render(): TemplateResult {
    if (this.bootError) {
      return html`<div class="gate">
        <div class="card error">Startup failed: ${this.bootError}</div>
      </div>`;
    }
    if (!this.ready || !this.auth) {
      return html`<div class="gate"><div class="card">Loading…</div></div>`;
    }
    if (!this.auth.authenticated) {
      return html`
        <div class="gate">
          <div class="card col" style="align-items:center">
            <h1 class="brand">newsfeed</h1>
            <p class="muted">Sign in to read your feeds.</p>
            <button class="primary" @click=${() => void this.auth?.login()}>
              Log in
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <header>
        <span class="brand">newsfeed</span>
        <label>Menu
          <input type="checkbox"/>
        </label>
        <nav>
          ${NAV.map(
            (item) =>
              html`<a
                href=${item.path}
                class=${this.isActive(item.path) ? "active" : ""}
                @click=${(e: Event) => this.go(e, item.path)}
                >${item.label}</a
              >`,
          )}
        </nav>
        ${this.auth.user?.email ? html`<span class="muted">${this.auth.user.email}</span>` : nothing}
        <button @click=${() => this.auth?.logout()}>Log out</button>
      </header>
      <main>${this.renderView()}</main>
    `;
  }

  private isActive(path: string): boolean {
    const name = this.route.name;
    if (path === "/") return name === "stream" || name === "feed";
    if (path === "/rules") return name === "rules" || name === "ruleActivity";
    return window.location.pathname === path;
  }
}

customElements.define("nf-app", NfApp);

declare global {
  interface HTMLElementTagNameMap {
    "nf-app": NfApp;
  }
}
